import { Component, HostBinding, Input } from '@angular/core';
import { Association, AssociationKeys } from '@ag-common-lib/lib/models/utils/association.model';
import { AddressModelKeys } from '@ag-common-lib/lib/models/utils/address.model';
import { GoogleMapsService } from 'ag-common-svc/lib/services';
import { COUNTRIES } from '@ag-common-lib/lib/lists/countries.list';
import { Address, Messages } from '@ag-common-lib/public-api';

@Component({
  selector: 'ag-association-address-form',
  templateUrl: './association-address-form.component.html',
  styleUrl: './association-address-form.component.scss',
})
export class AssociationAddressFormComponent {
  @HostBinding('class') className = 'association-address-form';
  private _associationFormData: Association;
  private readonly _proxiedAddresses = new WeakSet<object>();
  @Input() set associationFormData(value: Association) {
    this._associationFormData = value;

    // Reset Google address validation whenever any editable address field changes.
    if (this._associationFormData?.address) {
      if (!this._proxiedAddresses.has(this._associationFormData.address as unknown as object)) {
        this._associationFormData.address = this._createAddressProxy(this._associationFormData.address);
        this._proxiedAddresses.add(this._associationFormData.address as unknown as object);
      }
    }
  }
  get associationFormData(): Association {
    return this._associationFormData;
  }

  @Input() validationGroup: string;
  protected readonly AssociationKeys = AssociationKeys;
  protected readonly AddressModelKeys = AddressModelKeys;
  readonly countries = COUNTRIES;
  protected readonly ADDRESS_IS_INCOMPLETE_TEXT = Messages.ADDRESS_IS_INCOMPLETE_TEXT;
  protected readonly ADDRESS_HAS_UNCONFIRMED_DATA_TEXT = Messages.ADDRESS_HAS_UNCONFIRMED_DATA_TEXT;

  onAddressAutocompleteChange = e => {
    if (!!this.associationFormData?.address) {
      this.associationFormData.address[AddressModelKeys.address1] = e;
    }
  };

  protected readonly googleAddressValidatorAdapter = {
    getValue: () => {
      return this.associationFormData?.address as Partial<Address>;
    },
    bypass: () => {
      return !!this.associationFormData?.address?.[AddressModelKeys.validationResponse]?.result?.verdict;
    },
    reset: e => {
      console.log('ag-association-address-form reset', e);
    },
  };

  protected googleValidationCallback = () => {
    return this.checkAndConfirmValidation();
  };

  constructor(private googleMapsService: GoogleMapsService) {}

  private readonly addressFieldsToInvalidateValidation = [
    AddressModelKeys.address1,
    AddressModelKeys.address2,
    AddressModelKeys.city,
    AddressModelKeys.state,
    AddressModelKeys.zip,
    AddressModelKeys.country,
    AddressModelKeys.county,
  ];

  private _createAddressProxy(address: Address): Address {
    return new Proxy(address, {
      set: (target, prop: string | symbol, value, receiver) => {
        const propName = typeof prop === 'string' ? prop : null;
        const prevValue = propName ? target?.[propName] : undefined;
        const result = Reflect.set(target, prop, value, receiver);

        // Invalidate previous Google validation result when the relevant fields change.
        if (
          propName &&
          this.addressFieldsToInvalidateValidation.includes(propName as AddressModelKeys) &&
          value !== prevValue
        ) {
          const validationResponse = target?.[AddressModelKeys.validationResponse];
          if (validationResponse?.result) {
            target[AddressModelKeys.validationResponse] = {
              responseId: validationResponse?.responseId,
              result: null,
            };
          }
        }

        return result;
      },
    });
  }

  protected handleAddressSelectionChange = (address: Partial<Address>): void => {
    if (!this.associationFormData?.address) {
      return;
    }
    Object.assign(this.associationFormData.address, address);
  };

  private checkAndConfirmValidation = async (): Promise<boolean> => {
    if (this.associationFormData?.address?.[AddressModelKeys.validationResponse]?.result?.verdict) {
      return true;
    }

    if (!this.associationFormData?.address) {
      return false;
    }

    const validationResponse = await this.googleMapsService.validateAddress(this.associationFormData.address);

    this.associationFormData.address[AddressModelKeys.validationResponse] = validationResponse;

    const verdict = this.associationFormData?.address?.[AddressModelKeys.validationResponse]?.result?.verdict;
    const isCompleted = verdict?.addressComplete ?? false;
    const hasUnconfirmedComponents = verdict?.hasUnconfirmedComponents ?? false;

    return isCompleted && !hasUnconfirmedComponents;
  };

  setState = (state): void => {
    Object.assign(this.associationFormData.address, { state });
  };
}
